import Work from './caravan/work';
import Attributes from './caravan/attributes';
import banker from './caravan/banker';
import farmer from './caravan/farmer';
import carpenter from './caravan/carpenter';
import hunter from './caravan/hunter';
import preacher from './caravan/preacher';

class Caravan {
  static START_PARTY_SIZE = 1;
  static MAX_PARTY_SIZE = 6;
  static DEFAULT_CAPACITY = 6;

  static Background = {
    banker: banker,
    carpenter: carpenter,
    farmer: farmer,
    hunter: hunter,
    preacher: preacher
  };

  static Levels = {
    Walking: 0,
    PullingCarts: 14,
    Donkeys: 64,
    Horses: 124,
    HorseWagons: 244,
    OxWagons: 364
  };

  constructor() {
    this.level = Caravan.Levels.Walking;
    this.capacity = Caravan.DEFAULT_CAPACITY;

    this.members = this.initMembers();
    this.work = this.initWork();
    this.attributes = this.initAttributes();

    this.randomizeMembers(Caravan.START_PARTY_SIZE);
  }

  initAttributes() {
    const result = {};
    Object.keys(Attributes).forEach(attribute => {
      result[attribute] = { ...Attributes[attribute].default };
    });
    return result;
  }

  randomizeMembers(partySize) {
    // Randomly populate party
    const jobs = this.jobs();
    for (let i = 0; i < partySize; i++) {
      const job = Math.floor(Math.random() * jobs.length);
      this.update(jobs[job], 1);
    }
  }

  initWork() {
    const result = {
      production: new Map(),
      workers: new Map(),
      numWorkers: 0
    };

    Object.keys(Work).forEach(job => {
      result.workers.set(job, []);
      result.production.set(job, 0);
    });

    return result;
  }

  updateWork(member, job, delta) {
    let workers = this.work.workers.get(job);
    let production = this.work.production.get(job);
    if (delta > 0) {
      workers.push(member);
      production += Caravan.Background[member].work[job];
    } else {
      let index = workers.indexOf(member);
      if (index > -1) {
        workers.splice(index, 1);
        production -= Caravan.Background[member].work[job];
      }
    }

    this.work.numWorkers += delta;
    this.work.workers.set(job, workers);
    this.work.production.set(job, production);
  }

  initMembers() {
    const init = {};
    this.jobs().forEach(bg => (init[bg] = 0));
    return init;
  }

  bonuses() {
    return {
      food: this.attributes.startBonus.food,
      coins: this.attributes.startBonus.coins,
      ammo: this.attributes.startBonus.ammo,

      charisma: this.attributes.charisma,
      luck: this.attributes.luck,
      craftsmanship: this.attributes.craftsmanship
    };
  }

  additive(existing = 0, delta, sign) {
    return existing + sign * delta;
  }
  multiplicative(existing = 1, delta, sign) {
    if (sign) return existing * delta;
    return existing / delta;
  }
  updateAttribute(attribute, delta, sign, isAdditive) {
    this.attributes[attribute] = this.attributes[attribute] || {};
    Object.keys(delta).forEach(key => {
      const existing = this.attributes[attribute][key];
      const fn = isAdditive ? this.additive : this.multiplicative;
      this.attributes[attribute][key] = fn(existing, delta[key], sign);
    });
  }
  workers(background) {
    let result = 0;
    Object.keys(Work).forEach(job => {
      result += this.work.workers.get(job).filter(bg => bg === background).length;
    });
    return result;
  }
  workersInAJob(background, work) {
    return this.work.workers.get(work).filter(bg => bg === background).length;
  }

  consumption(isInCity) {
    return {
      food: this.attributes[Attributes.consume.name].food,
      water: isInCity ? 0 : this.attributes[Attributes.consume.name].water
    };
  }

  update(job, delta) {
    this.members[job] = this.job(job) + delta;

    // Update workers if we remove this member
    if (delta < 0 && this.workers(job) > this.members[job]) {
      let work = Work.forage;
      if (this.workersInAJob(job, Work.fabricate) > 0) {
        work = Work.fabricate;
      } else if (this.workersInAJob(job, Work.craft) > 0) {
        work = Work.craft;
      }
      this.updateWork(job, work, delta);
    }

    // Update attributes
    Object.keys(Attributes).forEach(attribute => {
      const isAdditive = Attributes[attribute].isAdditive;

      this.updateAttribute(attribute, Caravan.Background[job][attribute], delta, isAdditive);
    });
  }

  levelUp() {
    this.level++;
  }

  travelMethod() {
    if (this.level < Caravan.Levels.PullingCarts) return Caravan.Levels.Walking;
    if (this.level < Caravan.Levels.Donkeys) return Caravan.Levels.PullingCarts;
    if (this.level < Caravan.Levels.Horses) return Caravan.Levels.Donkeys;
    if (this.level < Caravan.Levels.HorseWagons) return Caravan.Levels.Horses;
    if (this.level < Caravan.Levels.OxWagons) return Caravan.Levels.HorseWagons;
    return Caravan.Levels.HorseAndOxWagons;
  }

  eat() {
    return this.attributes[Attributes.consume.name].food;
  }
  drink() {
    return this.attributes[Attributes.consume.name].water;
  }
  dehydration() {
    return this.attributes[Attributes.resilience.name].water;
  }
  starvation() {
    return this.attributes[Attributes.resilience.name].food;
  }

  jobs() {
    return Object.keys(Caravan.Background);
  }

  job(job) {
    return this.members[job];
  }

  size() {
    return Object.values(this.members).reduce((acc, cur) => acc + cur);
  }
  max() {
    return Caravan.MAX_PARTY_SIZE + this.travelMethod();
  }

  isFull() {
    return this.size() === Caravan.MAX_PARTY_SIZE;
  }
}

export default Caravan;
